import {
	Button,
	Group,
	LoadingOverlay,
	Modal,
	Space,
	Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCancel } from '@tabler/icons-react';
import type { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import { useUpsertSupplierForm } from '../hooks/useUpsertSupplierForm';
import type { SupplierDto, UpsertSupplierDto } from '../types/SupplierDto';
import { UpsertSupplierForm } from './UpsertSupplierForm';

interface EditSupplierModalProps {
	close: () => void;
	opened: boolean;
	initialSupplier: UpsertSupplierDto;
	updateTableValue: Dispatch<SetStateAction<SupplierDto>>;
}

export function EditSupplierModal({
	close,
	opened,
	initialSupplier,
	updateTableValue,
}: EditSupplierModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertSupplierForm(initialSupplier);

	const newSupplierMutation = mutate<UpsertSupplierDto, SupplierDto>(
		`${organisation}/supplier/edit`,
		inputs,
	);

	const resetAndClose = () => {
		form.reset();

		close();
		closeSuccessModal();
	};

	const complete = () => {
		if (newSupplierMutation.data?.value) {
			updateTableValue(newSupplierMutation.data.value);
			form.setInitialValues(inputs);
		}

		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={resetAndClose}
				title={<Title label={'Edit Supplier'} />}
				w={100}
				withCloseButton={true}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={newSupplierMutation.isLoading} />
				<Modal
					opened={showSuccessModal}
					onClose={complete}
					title={<Title label={'Supplier Edited!'} />}
					centered
				>
					<Text>
						Supplier for {inputs.name} has been successfully edited. Click Ok to
						continue...
					</Text>
					<Group justify={'flex-end'}>
						<Button
							color={'medepos.1'}
							variant={'filled'}
							leftSection={<IconCancel {...defaultIconStyle} />}
							onClick={() => complete()}
						>
							Ok
						</Button>
					</Group>
				</Modal>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newSupplierMutation.mutateAsync().then((response) => {
							if (response.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<UpsertSupplierForm
						form={form}
						selectedBranchIds={inputs.branchIds}
					/>
					<Space h={20} />
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
